import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import Vue from 'vue';
import { hasPendingImportRows } from '@/utils/sde-bulk-utils';
import { buildMetadataFieldInfoMap } from '@/utils/sde-row-utils';
import { generateDraftId } from '@/composables/useStructuredDataDraft';
import { csvMatrixToSdeRowsAsync } from '@/utils/structured-data-csv-bridge';
import { filterSdeConfigToMappedColumns } from '@/utils/sde-config-builder';
import { documentPreviewFromSession } from '@/utils/extraction-document-preview';
import structuredDataEditor from './structured-data-editor';

const STOCKPRODUCTS_CHUNK_SIZE = 200;

function chunkArray(items, chunkSize) {
    const chunks = [];
    for (let offset = 0; offset < items.length; offset += chunkSize) {
        chunks.push(items.slice(offset, offset + chunkSize));
    }
    return chunks;
}

async function postSyncChunk(axios, type, requestParams, payloadChunk) {
    return axios.post(`/api/sync/${type}/`, {
        ...requestParams,
        payload: payloadChunk,
    });
}

function syncErrorApplyOptions(state, rootState) {
    return {
        metadataFieldInfoMap: buildMetadataFieldInfoMap(rootState.producttypes?.producttypes),
        columns: state.structuredDataEditor?.config?.columns || [],
    };
}

function csvHeaderMatchesField(cell, field) {
    if (cell == null || cell === "") {
        return false;
    }
    const normalized = String(cell).trim().toLowerCase();
    const candidates = [
        field.value,
        field.text,
        field.header,
        ...(field.headers || []),
    ]
        .filter(Boolean)
        .map((value) => String(value).trim().toLowerCase());
    return candidates.includes(normalized);
}

function mapErrors(payload, error, offset = 0) {
    if (!error?.payload) {
        return null;
    }
    error.payload.forEach((item, index) => {
        const payloadIndex = offset + index;
        // Handle different error formats
        if (item.error) {
            payload[payloadIndex] = {
                errors: { non_field_errors: [item.error] },
                item: payload[payloadIndex] || {},
            };
        } else if (item.errors) {
            payload[payloadIndex] = {
                errors: item.errors,
                item: payload[payloadIndex] || item.item || {},
            };
        } else {
            payload[payloadIndex] = {
                errors: item,
                item: payload[payloadIndex] || {},
            };
        }
    });
    return payload;
}

function convertPayload(fields, data, fields_mapping) {
    var payload = [];
    console.log(fields, data, fields_mapping);
    data.forEach((item) => {

        var mapped_item = {};
        var index = 0;

        item.forEach((col) => {
            if (col == "") {
                col = null;
            }

            if (fields_mapping[index]) {
                let field = fields.find((item) => item.value == fields_mapping[index]);
                let dict = mapped_item;
                let parts = fields_mapping[index].split(".");
                let last_field = parts[parts.length - 1];
                parts = parts.slice(0, parts.length - 1);
                parts.forEach((field) => {
                    if (!dict[field]) {
                        dict[field] = {};
                    }
                    dict = dict[field];
                })
                console.log(col, field.type);
                if (field.type == "number") {
                    const normalized = String(col).trim().replace(",", ".");
                    col = Number.parseFloat(normalized);
                } else if (field.type == "boolean") {
                    if (col !== null) {
                        // Convert to lowercase for comparison but also check capitalized versions
                        const colLower = col.toLowerCase();
                        // Check for "false" values first (Non, non, false, etc.)
                        if (colLower == "false" ||
                            colLower == "no" ||
                            colLower == "0" ||
                            colLower == Vue.prototype.$translate("false") ||
                            colLower == Vue.prototype.$translate("no")
                        ) {
                            col = false;
                        } else {
                            // Check for "true" values (Oui, oui, true, yes, etc.)
                            col = (
                                colLower == "1" ||
                                colLower == "true" ||
                                colLower == "yes" ||
                                colLower == Vue.prototype.$translate("true") ||
                                colLower == Vue.prototype.$translate("yes")
                            );
                        }
                    }
                } else if (field.type == "phone number" || field.type == "phone_number" ||
                           field.value == "phone_number" || field.value == "mobile_phone_number" ||
                           last_field == "phone_number" || last_field == "mobile_phone_number") {
                    if (col !== null && typeof col === "string") {
                        // Remove all spaces from phone number
                        col = col.replace(/\s+/g, "");
                    }
                }

                dict[last_field] = col;
            }
            index += 1;
        });
        payload.push(mapped_item);
    });
    return payload;
}

const createState = () => ({
    file: null,
    data: null,
    step: 'selectfile', // selectfile, configure, synchronizing, results
    error: false,
    server_error: false,
    global_errors: null,
    payload: null,
    fields: [],
    fields_mapping: [],
    ignore_first_line: false,
    extra_params: {},
    syncProgress: null,
});

const mutations = {
    updateStep(state, step) {
        state.step = step;
    },
    updateError(state, error) {
        state.error = error;
    },
    updateServerError(state, server_error) {
        state.server_error = server_error;
    },
    updateFile(state, file) {
        state.file = file;
    },
    updateData(state, data) {
        state.data = data;
    },
    updatePayload(state, payload) {
        state.payload = payload;
    },
    updateFieldsMapping(state, fields_mapping) {
        state.fields_mapping = fields_mapping;
    },
    updateFieldMapping(state, params) {
        state.fields_mapping.forEach((item, item_index) => {
            if (item_index != params.index) {
                if (params.value == item) {
                    state.fields_mapping[item_index] = null;
                }
            } else {
                state.fields_mapping[item_index] = params.value;
            }
        });
    },
    updateFields(state, fields) {
        state.fields = fields;
    },
    updateIgnoreFirstLine(state, ignore_first_line) {
        state.ignore_first_line = ignore_first_line;
    },
    updateGlobalErrors(state, global_errors) {
        state.global_errors = global_errors;
    },
    updateExtraParams(state, extra_params) {
        state.extra_params = extra_params;
    },
    updateSyncProgress(state, syncProgress) {
        state.syncProgress = syncProgress;
    },
}

const actions = {

    validateFieldsMapping(state) {
        const missing_fields = [];
        state.fields.forEach((field) => {
            if (field.mandatory && !state.fields_mapping.includes(field.value)) {
                missing_fields.push(field.text);
            }
        });
        if (missing_fields.length) {
            throw {
                code: 400,
                success: false,
                details: Vue.prototype.$translate("theFollowingMandatoryFieldsHaveNotBeenMapped") + " " + missing_fields.join(", "),
            };
        }
    },

    changeData({ commit, dispatch, state }, params) {
        commit('updateData', params.data);
        commit('updateStep', 'configure');
        dispatch("guessFieldsMapping");
    },

    async prepareEditorFromCsv({ commit, state }, { sdeConfig }) {
        actions.validateFieldsMapping(state);
        const data = state.ignore_first_line ? state.data.slice(1) : state.data;
        const editorConfig = filterSdeConfigToMappedColumns(
            sdeConfig,
            state.fields_mapping
        );
        commit('structuredDataEditor/setConfig', editorConfig);
        commit('structuredDataEditor/setSessionId', null);
        commit('structuredDataEditor/setDraftId', generateDraftId());
        commit('structuredDataEditor/setDirty', false);
        commit('structuredDataEditor/resetNewRowsCounter');
        const rows = await csvMatrixToSdeRowsAsync(data, state.fields_mapping, state.fields);
        commit('structuredDataEditor/setRows', rows);
        commit('updateStep', 'edit');
    },

    prepareEditorFromExtraction({ commit, state }, { session, sdeConfig }) {
        const extraParams = {
            ...(state.extra_params || {}),
            ...(sdeConfig.extraParams || {}),
        };
        commit('updateExtraParams', extraParams);
        commit('structuredDataEditor/setConfig', {
            ...sdeConfig,
            ...documentPreviewFromSession(session),
        });
        commit('structuredDataEditor/setSessionId', session.id);
        commit('structuredDataEditor/setDraftId', session.id);
        commit('structuredDataEditor/setDirty', false);
        commit('structuredDataEditor/resetNewRowsCounter');
        commit('structuredDataEditor/setRows', []);
        commit('updateStep', 'edit');
    },

    openEditorForSession({ dispatch }, { session, sdeConfig }) {
        return dispatch('prepareEditorFromExtraction', { session, sdeConfig });
    },

    async synchronize({ commit, dispatch, state, rootState }, params) {
        const fromEditor = Boolean(params.useExistingPayload);
        if (!fromEditor) {
            actions.validateFieldsMapping(state);
        }

        // Also when importing from the editor: the stepper shows the loader
        // on the import step, and moves back to "edit" on error or partial.
        commit('updateStep', 'synchronizing');
        commit('updateSyncProgress', null);

        let payload = fromEditor
            ? state.payload
            : convertPayload(
                state.fields,
                state.ignore_first_line ? state.data.slice(1) : state.data,
                state.fields_mapping);
        commit('updatePayload', payload);

        let request_params = Object.assign({}, state.extra_params, { payload });
        const shouldChunk = (
            params.type === 'stockproducts'
            && payload.length > STOCKPRODUCTS_CHUNK_SIZE
        );
        let chunkOffset = 0;

        try {
            let response;
            if (shouldChunk) {
                const chunks = chunkArray(payload, STOCKPRODUCTS_CHUNK_SIZE);
                const mergedPayload = [];
                for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex += 1) {
                    commit(
                        'updateSyncProgress',
                        Math.round((chunkIndex / chunks.length) * 100)
                    );
                    try {
                        response = await postSyncChunk(
                            axios,
                            params.type,
                            state.extra_params,
                            chunks[chunkIndex],
                        );
                    } catch (chunkError) {
                        chunkError.chunkOffset = chunkOffset;
                        throw chunkError;
                    }
                    const chunkPayload = response.data.payload || [];
                    mergedPayload.push(...chunkPayload);
                    chunkOffset += chunks[chunkIndex].length;
                }
                commit('updateSyncProgress', 100);
                response = { data: { payload: mergedPayload } };
            } else {
                response = await axios.post(`/api/sync/${params.type}/`, request_params);
            }

            commit('updatePayload', response.data.payload);
            commit('updateError', false);
            commit('updateGlobalErrors', null);
            dispatch('session/fetchStats', null, { root: true });

            if (fromEditor) {
                commit('structuredDataEditor/markRowsFromSyncPayload', {
                    syncPayload: response.data.payload,
                    ...syncErrorApplyOptions(state, rootState),
                });
                if (hasPendingImportRows(state.structuredDataEditor.rows)) {
                    commit('updateStep', 'edit');
                    return { partial: true, payload: response.data.payload };
                }
            }

            const sessionId = state.structuredDataEditor?.sessionId;
            if (sessionId) {
                await dispatch(
                    'extractionSession/finalize',
                    { importSuccess: true },
                    { root: true }
                );
            }
            commit('updateStep', 'results');
            commit('updateSyncProgress', null);
            return { partial: false, payload: response.data.payload };
        } catch (xhr_error) {
            commit('updateSyncProgress', null);
            commit('updateError', true);

            let error = utils.handleError(xhr_error);
            commit('updateServerError', Number.parseInt(error.code / 100) == 5);

            if (Number.parseInt(error.code / 100) == 4) {
                const errorOffset = xhr_error.chunkOffset || 0;
                let payload_errors = mapErrors(state.payload, error.details, errorOffset);
                if (payload_errors) {
                    commit('updatePayload', payload_errors);
                    if (fromEditor) {
                        commit('structuredDataEditor/markRowsFromSyncPayload', {
                            syncPayload: payload_errors,
                            persistSuccess: false,
                            ...syncErrorApplyOptions(state, rootState),
                        });
                        commit('structuredDataEditor/setDirty', true);
                    }
                } else {
                    if (error.details.detail) {
                        commit('updateGlobalErrors', [error.details.detail]);
                    } else if (error.details.non_field_errors) {
                        commit('updateGlobalErrors', error.details.non_field_errors);
                    } else {
                        commit('updateGlobalErrors', [Vue.prototype.$translate("anUnexpectedErrorHappenedPleaseContactUsForSupport")]);
                    }
                }
            } else {
                commit('updateGlobalErrors', []);
            }

            commit('updateStep', fromEditor ? 'edit' : 'results');
            throw error;
        }
    },

    async init({ commit, dispatch, state }, params) {
        await commit('updateStep', 'selectfile');
        await commit('updateFile', null);
        await commit('updateData', null);
        await commit('updatePayload', null);
        await commit('updateFields', null);
        //await commit('updateFieldsMapping', []);
        await commit('updateIgnoreFirstLine', false);
        await commit('updateGlobalErrors', null);
        await commit('updateSyncProgress', null);
        await commit('updateExtraParams', params?.extra_params);
    },

    guessFieldsMapping({ commit, dispatch, state }, params) {
        const first_line = state.data[0];
        let mapping_found = false;
        let fields_mapping = [];
        first_line.forEach((cell) => {
            const field = state.fields.find((field) => csvHeaderMatchesField(cell, field));
            fields_mapping.push(field ? field.value : null);
            if (field) {
                mapping_found = true;
            }
        })
        if (mapping_found) {
            commit('updateFieldsMapping', fields_mapping);
            commit('updateIgnoreFirstLine', mapping_found);
        }
    },
};


export default () => ({
    namespaced: true,
    state: createState(),
    mutations,
    actions,
    modules: {
        structuredDataEditor: structuredDataEditor(),
    },
});